import React, { useEffect, useMemo, useRef } from 'react'
import { createPortal } from 'react-dom'
import { Box } from '@mui/material'
import { WherebyProvider } from '@whereby.com/browser-sdk/react'
import { useLocation } from 'react-router-dom'
import Draggable from 'react-draggable'
import { useGlobalWhereby } from '../../../../shared/hooks/useGlobalWhereby'
import { useWherebyRoom } from '../../../../shared/hooks/useWherebyRoom'
import { useWherebyCrossTabLeave } from '../../../../shared/hooks/useWherebyCrossTabLeave'
import { useLeaveGameCallWhenSessionCompleted } from '../../../../shared/hooks/useLeaveGameCallWhenSessionCompleted'
import { useWherebyCallOwnership } from '../../../../shared/hooks/useWherebyCallOwnership'
import useAppState from '../../../../hooks/useAppState'
import { WherebyCallSurface } from './WherebyCallSurface'
import { WherebyCallBlockedPanel } from './WherebyCallBlockedPanel'
import { WherebyFloatCardHeader, WherebyFloatPreparingRoom, WherebyMediaBlockedPanel } from './globalWherebyFloatParts'
import { useMediaPermissions } from '../../../../shared/hooks/useMediaPermissions'
import { buildFloatingCallScope } from '../../../../shared/hooks/wherebyCallScopes'
import { useGlobalWherebyFloatWatchdogs } from './useGlobalWherebyFloatWatchdogs'
import { useWherebyConnectionKey } from './wherebyCallReconnect'
import { isCallMediaBlocked, resolveCallView } from './wherebyCallHelpers'
import FloatingCallButton from '../FloatingCallButton'
import { INTERAX_COLORS, INTERAX_SHADOWS } from '../../../../constants/colors'
import { useTranslation } from '../../../../shared/hooks/useIntractionTranslate'
import { SharedRoutes } from '../../../../config/routes/routeEnums'
import { WherebyCallOwnershipStatus, WherebyFloatCardView } from '../../../../../../pure-js/enums/Whereby'
import { getWherebyCallDisplayName } from '../../../../../../pure-js/libs/isWherebyBotParticipant'

const DEFAULT_CARD_POSITION_PX = 16 // 16 px
const FLOATING_CARD_HEIGHT_PX = 380 // 380 px
const FLOATING_CARD_WIDTH_PX = 380 // 380 px
const FLOATING_CARD_Z_INDEX = 1_200 // Application overlay layer
const getDefaultCardPosition = () =>
  typeof window === 'undefined'
    ? { x: DEFAULT_CARD_POSITION_PX, y: DEFAULT_CARD_POSITION_PX }
    : {
        x: DEFAULT_CARD_POSITION_PX,
        y: Math.max(DEFAULT_CARD_POSITION_PX, window.innerHeight - FLOATING_CARD_HEIGHT_PX)
      }

export const GlobalWherebyFloat: React.FC = () => {
  const { t: translate } = useTranslation()
  const location = useLocation()
  const {
    isCallActive,
    isCallMinimized,
    floatingButtonPosition,
    minimizeCall,
    restoreCall,
    setPosition,
    killCall,
    setParticipants,
    callScope
  } = useGlobalWhereby()
  const {
    meetingInfo,
    isLoading,
    ensureRoom,
    refetch: refetchRoom,
    isEnsuring,
    roomUnavailableForRoute,
    hasScope,
    teamId
  } = useWherebyRoom()
  const { state: appState } = useAppState()
  const isPlayingGamePage =
    location.pathname.includes(SharedRoutes.PLAY_GAME_WITH_INTRODUCTION) ||
    location.pathname.includes(SharedRoutes.PLAY_GAME_WITHOUT_INTRODUCTION)
  // Drop a stale game call when a sibling tab signals that the game is over.
  useWherebyCrossTabLeave()
  // Backstop: leave when the backend marks the pinned game session completed (reaches other devices too).
  useLeaveGameCallWhenSessionCompleted()
  // react-draggable must receive nodeRef in React 19; otherwise it falls back to findDOMNode and crashes.
  const draggableNodeRef = useRef<HTMLDivElement>(null)

  // Claim ownership before mounting the only live Whereby connection for this user.
  const ownershipScope = useMemo(
    () =>
      buildFloatingCallScope({
        teamId,
        teamName: meetingInfo?.teamName,
        callScope,
        currentPathname: location.pathname
      }),
    [teamId, meetingInfo?.teamName, callScope, location.pathname]
  )
  const ownership = useWherebyCallOwnership({ enabled: isCallActive, scope: ownershipScope })
  const mediaPermissions = useMediaPermissions()
  const isCallOwnedByThisTab = isCallActive && ownership.canConnect

  const watchdogs = useGlobalWherebyFloatWatchdogs({
    isCallOwnedByThisTab,
    hasScope,
    roomUrl: meetingInfo?.roomUrl,
    scopeKey: teamId,
    isEnsuringRoom: isEnsuring,
    ensureRoom,
    refetchRoom
  })

  const { connectionKey, startFreshConnection } = useWherebyConnectionKey(meetingInfo?.roomUrl)

  // Restore once when another client blocks a minimized call so the takeover prompt is visible.
  const previousOwnershipStatusRef = useRef(ownership.status)
  useEffect(() => {
    const previousStatus = previousOwnershipStatusRef.current
    previousOwnershipStatusRef.current = ownership.status
    const justBecameBlocked =
      ownership.status === WherebyCallOwnershipStatus.BLOCKED && previousStatus !== WherebyCallOwnershipStatus.BLOCKED
    if (justBecameBlocked && isCallMinimized) restoreCall()
  }, [ownership.status, isCallMinimized, restoreCall])

  // Clear shared participants when the call is not active, so the game never sees stale presence.
  useEffect(() => {
    if (!isCallActive) setParticipants([])
  }, [isCallActive, setParticipants])

  if (!isCallActive) return null
  // Only mount a floating call on a route that owns the resolved activity.
  if (roomUnavailableForRoute) return null
  // Do not show room preparation when there is no activity scope.
  if (!isLoading && !hasScope) return null

  const roomUrl = meetingInfo?.roomUrl
  // Our user id from global state (authoritative) — used as the local participant's Whereby externalId.
  const userId = appState.user?.id || null
  const displayName = meetingInfo?.displayName || getWherebyCallDisplayName(appState.user)
  const canInitializeWhereby = Boolean(roomUrl && userId && displayName)

  const cardView = resolveCallView(ownership.status, canInitializeWhereby)
  // Denied devices must block signaling so roster presence cannot exist without usable media.
  const isMediaBlocked = isCallMediaBlocked(mediaPermissions)
  const shouldShowOwnershipPrompt = cardView === WherebyFloatCardView.ASK
  const shouldShowBlockedMedia = cardView === WherebyFloatCardView.CALL && Boolean(roomUrl) && isMediaBlocked
  const shouldShowCall =
    cardView === WherebyFloatCardView.CALL &&
    Boolean(roomUrl) &&
    Boolean(userId) &&
    !mediaPermissions.isLoading &&
    !isMediaBlocked
  const isCheckingCallPermissions = cardView === WherebyFloatCardView.CALL && mediaPermissions.isLoading
  const shouldShowRoomPreparation = cardView === WherebyFloatCardView.PREPARING || isCheckingCallPermissions
  const renderCallSurface = () => {
    if (!shouldShowCall || !roomUrl || !userId) return null
    return (
      <WherebyProvider key={connectionKey}>
        <WherebyCallSurface
          roomUrl={roomUrl}
          teamName={meetingInfo?.teamName ?? ''}
          displayName={displayName}
          userId={userId}
          onParticipantsChange={setParticipants}
          onMinimize={minimizeCall}
          onReconnect={startFreshConnection}
          onLeaveCall={killCall}
          compact={true}
          showHangupButton={!isPlayingGamePage}
        />
      </WherebyProvider>
    )
  }

  // Keep the call mounted and hide only its card while minimized.
  return (
    <>
      {createPortal(
        <Draggable
          nodeRef={draggableNodeRef}
          handle=".whereby-drag-handle"
          cancel="button, [role='button']"
          defaultPosition={getDefaultCardPosition()}
          bounds="body"
        >
          <Box
            ref={draggableNodeRef}
            sx={{
              display: isCallMinimized ? 'none' : undefined,
              position: 'fixed',
              top: 0,
              left: 0,
              width: { xs: '92vw', sm: `${FLOATING_CARD_WIDTH_PX}px`, md: `${FLOATING_CARD_WIDTH_PX}px` },
              borderRadius: '16px',
              backgroundColor: INTERAX_COLORS.offBlack,
              border: `1px solid ${INTERAX_COLORS.white15}`,
              zIndex: FLOATING_CARD_Z_INDEX,
              overflow: 'hidden',
              boxShadow: INTERAX_SHADOWS.wherebyFloat,
              p: 2,
              boxSizing: 'border-box'
            }}
          >
            {shouldShowOwnershipPrompt ? (
              <>
                <WherebyFloatCardHeader onMinimize={minimizeCall} />
                <WherebyCallBlockedPanel
                  status={ownership.status}
                  onConfirmTakeover={ownership.confirmTakeover}
                  onRetry={ownership.retry}
                  onCancel={killCall}
                />
              </>
            ) : null}

            {shouldShowBlockedMedia ? <WherebyMediaBlockedPanel onLeaveCall={killCall} /> : null}
            {renderCallSurface()}

            {shouldShowRoomPreparation ? (
              <>
                <WherebyFloatCardHeader onMinimize={minimizeCall} />
                <WherebyFloatPreparingRoom
                  isTakingTooLong={watchdogs.isPreparingTooLong}
                  onRetry={watchdogs.retryRoom}
                  onLeaveCall={killCall}
                />
              </>
            ) : null}
          </Box>
        </Draggable>,
        document.body
      )}
      {isCallMinimized
        ? createPortal(
            <FloatingCallButton
              ariaLabel={translate('gamer.gameCenter.restoreCall')}
              onRestore={restoreCall}
              position={floatingButtonPosition}
              onPositionChange={setPosition}
            />,
            document.body
          )
        : null}
    </>
  )
}
